import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { DocumentNavigator } from 'src/components/document-review/review/DocumentNavigator'
import { ReviewHeader } from 'src/components/document-review/review/ReviewHeader'
import { McDecisionSection } from 'src/components/document-review/review/master-contractor/McDecisionSection'
import { McReviewForm } from 'src/components/document-review/review/master-contractor/McReviewForm'
import { FileViewer } from 'src/components/shared/file-viewer/FileViewer'
import { Button } from 'src/components/shared/Button'
import { AdminFetchCustomers, AdminFetchMcReviewDocuments } from 'src/constants/actionTypes'
import { ACCEPTED_DOCUMENT_EXTENSIONS } from 'src/constants/document-review/constants'
import { DocumentReviewTab } from 'src/constants/document-review/enums'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { ADMIN_ROUTES } from 'src/constants/routes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { useHasPermission } from 'src/hooks/useHasPermission'
import { getCustomers } from 'src/selectors/customer'
import { getInternalIdentity } from 'src/selectors/internalAuth'
import {
  getMcDocumentReviewSubmitting,
  getMcReviewDocuments,
  getMcReviewDocumentsError,
  getMcReviewDocumentsLoading,
} from 'src/selectors/mcDocumentReview'
import { buildCustomerNameById, resolveMcDisplayName } from 'src/utils/documentReviewList'

const QUEUE_URL = `${ADMIN_ROUTES.INTERNAL_DOCUMENT_REVIEW_URL}?tab=${DocumentReviewTab.MasterContractor}`

const McReviewPageComponent = () => {
  const { collectedDataId } = useParams<{ collectedDataId: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const [documentIndex, setDocumentIndex] = useState<number>(0)

  const { isAuthenticated } = useAuthSession()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()

  const documents = useSelector(getMcReviewDocuments)
  const isLoading = useSelector(getMcReviewDocumentsLoading)
  const error = useSelector(getMcReviewDocumentsError)
  const isSubmitting = useSelector(getMcDocumentReviewSubmitting)
  const customers = useSelector(getCustomers)
  // True once the queue fetch below has settled (success or failure) at least once. Distinguishes
  // "the queue genuinely has no such row" from "the fetch a deep link kicks off just hasn't
  // resolved yet" — without it, the very first paint of a pasted link (before the effect below
  // even runs) would render the "no longer in the queue" copy for a document that simply hasn't
  // loaded yet.
  const [hasCheckedQueue, setHasCheckedQueue] = useState<boolean>(documents.length > 0)
  // `useAuthSession` only answers whether a session exists — the signed-in identity itself
  // (WorkOS AuthKit + /v1/internal/auth/me) lives in the internalAuth slice.
  const identity = useSelector(getInternalIdentity)
  const reviewedByUser = identity?.email ?? ''
  const canViewPartners = useHasPermission(InternalPermission.PartnersView)

  const customerNameById = useMemo(() => {
    return buildCustomerNameById(customers)
  }, [customers])

  // No detail endpoint exists for the MC queue, so a deep link or a refresh has to fetch the
  // whole list and find the row in it.
  useEffect(() => {
    if (!isAuthenticated || documents.length > 0) {
      return
    }

    let isMounted = true

    asyncDispatchWithErrorToast(AdminFetchMcReviewDocuments.request()).then(() => {
      if (isMounted) {
        setHasCheckedQueue(true)
      }
    })

    return () => {
      isMounted = false
    }
  }, [asyncDispatchWithErrorToast, isAuthenticated, documents.length])

  // The MC rows carry only `partnerId`. The customer list needs `partners_view`, a different
  // permission than MC review, so it is skipped without it and the subtitle falls back to the raw
  // partner id (`resolveMcDisplayName`). See `DocumentReviewQueuePage` for why this is a plain
  // `dispatch`.
  useEffect(() => {
    if (!isAuthenticated || !canViewPartners || customers.length > 0) {
      return
    }

    dispatch(AdminFetchCustomers.request())
  }, [dispatch, canViewPartners, isAuthenticated, customers.length])

  // Reset to the first file whenever the row changes, so advancing to the next review does not
  // land on document 3 of a row that has one.
  useEffect(() => {
    setDocumentIndex(0)
  }, [collectedDataId])

  const row = useMemo(() => {
    return documents.find((document) => {
      return document.id === collectedDataId
    })
  }, [documents, collectedDataId])

  const handleBack = useCallback(() => {
    navigate(QUEUE_URL)
  }, [navigate])

  const handleRetry = useCallback(() => {
    asyncDispatchWithErrorToast(AdminFetchMcReviewDocuments.request()).then(() => {
      setHasCheckedQueue(true)
    })
  }, [asyncDispatchWithErrorToast])

  const goToNextInQueue = useCallback(() => {
    const currentIndex = documents.findIndex((document) => {
      return document.id === collectedDataId
    })
    // The decided row is about to leave the queue on refetch, so "next" is the row that follows
    // it in the list we have now.
    const nextRow = documents[currentIndex + 1] ?? documents[0]

    if (!nextRow || nextRow.id === collectedDataId) {
      navigate(QUEUE_URL)

      return
    }

    navigate(ADMIN_ROUTES.INTERNAL_DOCUMENT_REVIEW_MC_URL.replace(':collectedDataId', nextRow.id))
  }, [navigate, documents, collectedDataId])

  const goToQueueOffset = useCallback(
    (offset: number) => {
      const currentIndex = documents.findIndex((document) => {
        return document.id === collectedDataId
      })
      const targetRow = currentIndex === -1 ? undefined : documents[currentIndex + offset]

      if (!targetRow) {
        return
      }

      navigate(
        ADMIN_ROUTES.INTERNAL_DOCUMENT_REVIEW_MC_URL.replace(':collectedDataId', targetRow.id),
      )
    },
    [navigate, documents, collectedDataId],
  )

  const handlePreviousInQueue = useCallback(() => {
    goToQueueOffset(-1)
  }, [goToQueueOffset])

  const handleNextInQueue = useCallback(() => {
    goToQueueOffset(1)
  }, [goToQueueOffset])

  const handleDecided = useCallback(() => {
    // Fired and not awaited: `goToNextInQueue` deliberately reads the queue as it stands right
    // now (see its own comment) — waiting for this refetch would mean navigating with a queue
    // that already dropped the just-decided row.
    asyncDispatchWithErrorToast(AdminFetchMcReviewDocuments.request())
    goToNextInQueue()
  }, [asyncDispatchWithErrorToast, goToNextInQueue])

  const handleSkip = useCallback(() => {
    goToNextInQueue()
  }, [goToNextInQueue])

  const handlePreviousDocument = useCallback(() => {
    setDocumentIndex((previousIndex) => {
      return Math.max(0, previousIndex - 1)
    })
  }, [])

  const rowDocuments = row?.documents ?? []

  const handleNextDocument = useCallback(() => {
    setDocumentIndex((previousIndex) => {
      return Math.min(rowDocuments.length - 1, previousIndex + 1)
    })
  }, [rowDocuments.length])

  // Position comes from the loaded queue rather than being passed through navigation state, so it
  // survives a refresh and a pasted link.
  const queuePosition = useMemo(() => {
    const index = documents.findIndex((document) => {
      return document.id === collectedDataId
    })

    return index + 1
  }, [documents, collectedDataId])

  // `!hasCheckedQueue` covers the frame(s) before the fetch effect above has settled at all — the
  // fetch it triggers hasn't necessarily flipped `isLoading` to true yet on the very first paint
  // of a deep link, and without this the reviewer would briefly see "no longer in the queue" for
  // a document that just hasn't loaded.
  if (isLoading || !hasCheckedQueue) {
    return (
      <div css={tw`flex flex-1 items-center justify-center p-6`}>
        <Typography variant="body2" css={tw`text-shades-500`}>
          Loading document…
        </Typography>
      </div>
    )
  }

  // A failed queue fetch is an infrastructure problem, not evidence the document was decided —
  // conflating the two would tell the reviewer to walk away from something that just needs a
  // retry. Gated on `!row` too: `documents` survives a FAILED fetch (see the reducer), so a
  // background refetch failure while a valid row is already on screen must not replace it with
  // an error page the reviewer cannot work around — the failed action already raised its own
  // toast.
  if (error && !row) {
    return (
      <div
        data-testid="mc-review-load-error"
        css={tw`flex flex-1 flex-col items-center justify-center gap-4 p-6`}
      >
        <Typography variant="h4">This document could not be loaded</Typography>
        <Typography variant="body2" css={tw`text-shades-500`}>
          {error}
        </Typography>
        <div css={tw`flex flex-row gap-2`}>
          <Button size="small" color="white" onClick={handleRetry}>
            Try again
          </Button>
          <Button size="small" onClick={handleBack}>
            Back to queue
          </Button>
        </div>
      </div>
    )
  }

  if (!row) {
    return (
      <div
        data-testid="mc-review-not-in-queue"
        css={tw`flex flex-1 flex-col items-center justify-center gap-4 p-6`}
      >
        <Typography variant="h4">This document is no longer in the review queue</Typography>
        <Typography variant="body2" css={tw`text-shades-500`}>
          It may have been approved or rejected by someone else.
        </Typography>
        <Button size="small" onClick={handleBack}>
          Back to queue
        </Button>
      </div>
    )
  }

  const currentDocument = rowDocuments[documentIndex]
  const extension = currentDocument?.extension?.toLowerCase() ?? ''
  const isSupported = ACCEPTED_DOCUMENT_EXTENSIONS.includes(extension)

  return (
    <div
      data-testid="mc-review-page"
      css={tw`flex min-h-0 min-w-0 flex-1 flex-col gap-6 overflow-hidden p-6`}
    >
      <ReviewHeader
        title={row.type}
        subtitle={resolveMcDisplayName(row.partnerId, customerNameById)}
        position={queuePosition}
        total={documents.length}
        onBack={handleBack}
        onPrevious={handlePreviousInQueue}
        onNext={handleNextInQueue}
      />

      <div css={tw`grid min-h-0 flex-1 grid-cols-2 gap-6 overflow-hidden`}>
        <div css={tw`flex min-h-0 flex-col gap-3 overflow-hidden`}>
          <DocumentNavigator
            index={documentIndex}
            total={rowDocuments.length}
            onPrevious={handlePreviousDocument}
            onNext={handleNextDocument}
          />
          {currentDocument && isSupported ? (
            <FileViewer
              filePath={currentDocument.filePath}
              isPdf={extension === 'pdf'}
              fileName={currentDocument.fileName}
              fileExtension={extension}
            />
          ) : (
            <div
              css={tw`flex flex-1 items-center justify-center rounded-lg border border-solid border-shades-100 bg-shades-0`}
            >
              <Typography variant="body2" css={tw`text-shades-500`}>
                {currentDocument
                  ? 'This file type cannot be previewed.'
                  : 'No document was uploaded.'}
              </Typography>
            </div>
          )}
        </div>

        <div css={tw`flex min-h-0 flex-col gap-4 overflow-y-auto`}>
          {/* Keyed on the row id: deciding or skipping navigates in place on the same route, so
              without this key React would keep reusing the same Formik/modal instances across
              rows — the next document's form would open pre-filled with the previous one's
              expiry, document id, and any open reject-reason selection. */}
          <McReviewForm key={row.id}>
            <McDecisionSection
              partnerId={row.partnerId}
              partnerCollectedDataId={row.id}
              reviewedByUser={reviewedByUser}
              isSubmitting={isSubmitting}
              onDecided={handleDecided}
              onSkip={handleSkip}
            />
          </McReviewForm>
        </div>
      </div>
    </div>
  )
}

export const McReviewPage = memo(McReviewPageComponent)
